import { gql } from '@apollo/client'

// --- Shared error shapes ---

export type MaDebugError = {
  statusCode: number
  code: string | null
  message: string | null
}

export type MaDebugCallError = {
  operation: string
  message: string
  /** Upstream HTTP status, when the SDK attached one. */
  statusCode?: number | null
  /** Upstream error response body, when available. */
  detail?: string | null
}

export type MaNeptuneUser = {
  exists: boolean
  id: string | null
  mlm_username: string | null
  mlm_user_id: string | null
} | null

// --- Find loans (list) ---

/** Normalized display projection shared by both list sources (borrower + small-search). */
export type MaLoanListSummary = {
  posLoanId: string | null
  losRefNumber: string | null
  status: string | null
  formStatus: string | null
  loanType: string | null
  created: string | null
  submitted: string | null
  loanAmount: number | null
  propertyValue: number | null
  /** All applicants, each with their per-applicant status. Order is whatever the POS returned — NOT borrower-first. */
  applicants: MaLoanApplicant[]
  /** The assigned loan officer's email, projected from the raw row's `assignees` map. Null when unassigned. */
  loanOfficerEmail: string | null
}

export type MaLoanApplicant = {
  name: string | null
  email: string | null
  role: string | null
  status: string | null
}

export type MaLoanListItem = {
  summary: MaLoanListSummary
  /** Untouched upstream row (FullLoanData from borrower, or LoanSmallData from search). */
  raw: unknown
}

export type MaFindLoansPayload = {
  source: 'getLoansByBorrower' | 'searchLoansSmall'
  total: number | null
  loans: MaLoanListItem[]
  neptuneUser: MaNeptuneUser
  errors: MaDebugCallError[]
}

export type MaFindLoansVariables = {
  input: {
    email?: string
    loanIds?: string[]
    losNumbers?: string[]
    applicationStatuses?: string[]
    formStatuses?: string[]
    includeLeads?: boolean
    loanOfficerEmails?: string[]
    /** ISO datetime bounds for the loan's submitted date. */
    submittedFrom?: string
    submittedTo?: string
    /** ISO datetime bounds for the loan's created/started date. */
    createdFrom?: string
    createdTo?: string
    sortField?: 'created' | 'loanId'
    sortDirection?: 'asc' | 'desc'
    take?: number
    skip?: number
  }
}

export type MaFindLoansResponse = {
  mortgageAccessDebugFindLoans: {
    result: MaFindLoansPayload | null
    error: MaDebugError | null
  }
}

export const MORTGAGE_ACCESS_DEBUG_FIND_LOANS = gql`
  query mortgageAccessDebugFindLoans($input: MortgageAccessFindLoansInput!) {
    mortgageAccessDebugFindLoans(input: $input) {
      result
      error {
        statusCode
        code
        message
      }
    }
  }
`

// --- Single-loan detail (drill-down) ---

export type MaLoanDetailSummary = {
  posLoanId: string | null
  loanStatus: string | null
  losRefNumber: string | null
  meridianLinkSynced: boolean
  meridianLinkDocExportQueueCount: number | null
  loanAmount: number | null
  propertyValue: number | null
}

export type MaLoanDetailBlock = {
  loanId: string | null
  summary: MaLoanDetailSummary
  loanData: unknown
  loanApplication: unknown
  meridianLinkSyncState: unknown
  errors: MaDebugCallError[]
}

export type MaLoanDetailVariables = { input: { loanId: string } }

export type MaLoanDetailResponse = {
  mortgageAccessDebugLoanDetail: {
    result: MaLoanDetailBlock | null
    error: MaDebugError | null
  }
}

export const MORTGAGE_ACCESS_DEBUG_LOAN_DETAIL = gql`
  query mortgageAccessDebugLoanDetail($input: MortgageAccessLoanDetailInput!) {
    mortgageAccessDebugLoanDetail(input: $input) {
      result
      error {
        statusCode
        code
        message
      }
    }
  }
`

/** Application-status filter values accepted by the small-search path. */
export const MORTGAGE_ACCESS_APPLICATION_STATUSES = [
  'ApplicationSubmitted',
  'Processing',
  'Underwriting',
  'Documents',
  'Funding',
  'Suspended',
  'Closed',
  'Decline',
  'Cancelled',
] as const

/** Form-status filter values accepted by the small-search path. */
export const MORTGAGE_ACCESS_FORM_STATUSES = [
  'Open',
  'Application',
  'Decline',
  'PostApplication',
  'Cancelled',
  'Deleted',
  'Closed',
  'OnHold',
  'ApplicationClosed',
  'WelcomePostApplication',
  'WelcomePersonalDashboard',
  'Lead',
] as const
